import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PROFILE, STATS, SKILLS, PROCESS_GALLERY } from "../data/content";

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(start + (to - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return <span ref={ref}>{val}{suffix}</span>;
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#F2EBE5] text-[#0B0E14] py-24 md:py-36 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 md:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              <div className="absolute -inset-4 md:-inset-6 bg-[#D18A58]/20 -z-0 rotate-2" />
              <img
                src={PROFILE.about_image}
                alt={PROFILE.name}
                className="relative z-10 w-full aspect-[4/5] object-cover object-top grayscale-[0.15] saturate-[1.05]"
              />
              <div className="absolute z-10 bottom-4 left-4 right-4 flex justify-between items-end text-[10px] uppercase tracking-overline text-[#F2EBE5] mix-blend-difference">
                <span>Portrait · 2025</span>
                <span>{PROFILE.location}</span>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 lg:pl-8">
            <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#8A5A53] mb-5">
              <span className="h-px w-10 bg-[#D18A58]" />
              About · 02
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
            >
              An eye for the <span className="italic text-[#D18A58]">considered</span>, a love for the editorial.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 text-base md:text-lg text-[#5C5E68] leading-relaxed max-w-2xl"
            >
              {PROFILE.bio}
            </motion.p>

            <div className="mt-12 grid grid-cols-3 gap-6 md:gap-8 border-t border-[#0B0E14]/10 pt-10">
              {STATS.map((s, i) => (
                <motion.div
                  key={i}
                  data-testid={`stat-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                >
                  <div className="font-display text-5xl md:text-6xl tracking-tight text-[#0B0E14]">
                    <Counter to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-overline text-[#5C5E68]">{s.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="mt-12">
              <div className="text-[10px] uppercase tracking-overline text-[#5C5E68] mb-5">Technical Skills</div>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((s, i) => (
                  <motion.span
                    key={s}
                    data-testid={`skill-${i}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.04 }}
                    whileHover={{ y: -3, backgroundColor: "#D18A58", color: "#0B0E14" }}
                    className="rounded-full border border-[#0B0E14]/20 px-4 py-2 text-xs tracking-wide text-[#0B0E14] bg-white/50 backdrop-blur cursor-default"
                  >
                    {s}
                  </motion.span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Process micro-gallery */}
        <div className="mt-20 md:mt-28 border-t border-[#0B0E14]/10 pt-12">
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-overline text-[#8A5A53] mb-3">Process · Behind the scenes</div>
              <h3 className="font-display text-3xl md:text-4xl tracking-tight">From <span className="italic text-[#D18A58]">brief</span> to final edit.</h3>
            </div>
            <p className="text-sm text-[#5C5E68] max-w-sm">A glimpse into the way I work — research, casting, set and final edit.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {PROCESS_GALLERY.map((p, i) => (
              <motion.div
                key={i}
                data-testid={`process-tile-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="group relative aspect-[3/4] overflow-hidden rounded-sm bg-[#0B0E14]/5"
              >
                <img src={p.url} alt={p.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/70 to-transparent" />
                <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-overline text-[#F2EBE5]">{p.label}</div>
                <div className="absolute top-3 right-3 font-display italic text-xs text-[#F2EBE5]/70">0{i + 1}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
